import React, { useState, useEffect, useRef } from 'react';
import DataTable from '../../DataTable';

/**
 * 管理 - センサーイベントログページ
 */

// イベントタイプ一覧
const EVENT_TYPES = ['wash_start', 'wash_end', 'heartbeat', 'boot', 'wifi_reconnected', 'running', 'idle', 'alert', 'alert_clear'];

function EventLogs({
  theme,
  eventLogFilters,
  setEventLogFilters,
  eventLogColumns,
  eventLogs,
  defaultEventLogColumns,
  setEventLogColumns,
  eventLogSort,
  setEventLogSort,
  selectedEventLogIds,
  setSelectedEventLogIds,
  eventLogPage,
  eventLogPageSize,
  eventLogTotal,
  setEventLogPage,
  setEventLogPageSize,
  handleDeleteSelectedEventLogs,
  setJsonModalData,
  setShowJsonModal,
  handleReprocessEventLog,
  handleDeleteEventLog
}) {
  // MACアドレスのローカル入力管理（デバウンス用）
  const [localMacAddress, setLocalMacAddress] = useState(eventLogFilters.mac_address);
  const [eventTypeDropdownOpen, setEventTypeDropdownOpen] = useState(false);
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);

  // MACアドレス入力から500ms後に検索（デバウンス）
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setEventLogFilters(prev =>
        prev.mac_address === localMacAddress ? prev : { ...prev, mac_address: localMacAddress }
      );
      setEventLogPage(1);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [localMacAddress, setEventLogFilters, setEventLogPage]);

  // 外部から mac_address が変更された場合（センサー画面からのナビゲーション等）に同期
  useEffect(() => {
    setLocalMacAddress(eventLogFilters.mac_address);
  }, [eventLogFilters.mac_address]);

  // ドロップダウンの外クリックで閉じる
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setEventTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedTypes = Array.isArray(eventLogFilters.event_type) ? eventLogFilters.event_type : [];

  const toggleEventType = (type) => {
    const updated = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    setEventLogFilters(prev => ({ ...prev, event_type: updated }));
    setEventLogPage(1);
  };

  const displayLabel = selectedTypes.length === 0
    ? 'すべて'
    : selectedTypes.length === 1
      ? selectedTypes[0]
      : `${selectedTypes.length}件選択`;

  const formatDateTime = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('ja-JP');
  };

  // イベントログのカスタムセルレンダラー
  const renderEventLogCell = (log, column, cellStyle) => {
    const parsedPayload = log.raw_payload ? (typeof log.raw_payload === 'string' ? JSON.parse(log.raw_payload) : log.raw_payload) : {};
    
    switch (column.id) {
      case 'checkbox':
        return undefined; // DataTableに任せる
      case 'event_log_id':
        return <td key={column.id} style={cellStyle}>{log.event_log_id}</td>;
      case 'received_at':
        return <td key={column.id} style={cellStyle}>{formatDateTime(log.received_at)}</td>;
      case 'mac_address':
        return <td key={column.id} style={cellStyle}><code style={{ fontSize: '12px' }}>{log.mac_address}</code></td>;
      case 'event_type':
        return (
          <td key={column.id} style={cellStyle}>
            <span className={`badge ${
              log.event_type === 'wash_start' ? 'badge-success' :
              log.event_type === 'wash_end' ? 'badge-success' :
              log.event_type === 'heartbeat' ? 'badge-default' :
              log.event_type === 'boot' ? 'badge-warning' :
              log.event_type === 'wifi_reconnected' ? 'badge-warning' :
              'badge-default'
            }`}>
              {log.event_type}
            </span>
          </td>
        );
      case 'rfid_tag_id':
        return <td key={column.id} style={{...cellStyle, fontFamily: 'monospace', fontSize: '1.15em'}}>{log.rfid_tag_id || '-'}</td>;
      case 'is_processed':
        return (
          <td key={column.id} style={cellStyle}>
            {log.is_processed ? (
              <span className="badge badge-success">処理済み</span>
            ) : (
              <span className="badge badge-warning">未処理</span>
            )}
          </td>
        );
      case 'result_type':
        return (
          <td key={column.id} style={cellStyle}>
            {log.process_success === true && log.result_type === 'pending' && (
              <span className="badge badge-default">pending</span>
            )}
            {log.process_success === true && log.result_type !== 'pending' && (
              <span className="badge badge-success">{log.result_type || 'success'}</span>
            )}
            {log.process_success === false && (
              <span className="badge badge-danger">error</span>
            )}
            {log.process_success === null && '-'}
          </td>
        );
      case 'result_detail':
        return (
          <td key={column.id} style={cellStyle}>
            {log.error_message ? (
              <span style={{ color: '#e53e3e', fontSize: '12px' }} title={log.error_message}>
                {log.error_message.substring(0, 50)}{log.error_message.length > 50 ? '...' : ''}
              </span>
            ) : (log.raw_payload && log.raw_payload.message) ? (
              <span style={{ color: '#718096', fontSize: '12px' }} title={log.raw_payload.message}>
                {log.raw_payload.message.substring(0, 50)}{log.raw_payload.message.length > 50 ? '...' : ''}
              </span>
            ) : '-'}
          </td>
        );
      case 'json':
        return (
          <td key={column.id} style={cellStyle}>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => {
                setJsonModalData(log.raw_payload);
                setShowJsonModal(true);
              }}
              style={{ fontSize: '11px', padding: '4px 8px' }}
            >
              📄 表示
            </button>
          </td>
        );
      case 'cleaning_history_id':
        return <td key={column.id} style={cellStyle}>{parsedPayload.cleaning_history_id || '-'}</td>;
      case 'pending_event_id':
        return <td key={column.id} style={cellStyle}>{parsedPayload.pending_event_id || '-'}</td>;
      case 'timestamp':
        return <td key={column.id} style={cellStyle}>{formatDateTime(parsedPayload.timestamp)}</td>;
      case 'actions':
        return (
          <td key={column.id} style={cellStyle}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {log.process_success === false && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleReprocessEventLog(log.event_log_id)}
                >
                  再処理
                </button>
              )}
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleDeleteEventLog(log.event_log_id)}
              >
                削除
              </button>
            </div>
          </td>
        );
      default:
        return undefined; // デフォルトレンダリング
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>センサーイベントログ</h2>
      </div>
      
      {/* フィルタ */}
      <div style={{ 
        padding: '20px', 
        backgroundColor: theme === 'dark' ? '#2f3844' : '#f7fafc', 
        borderRadius: '8px', 
        marginBottom: '20px',
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-end',
        flexWrap: 'wrap'
      }}>
        {/* MACアドレス: 入力の都度デバウンス検索 */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: theme === 'dark' ? '#dae3ed' : '#2d3748' }}>
            MACアドレス
          </label>
          <input
            type="text"
            value={localMacAddress}
            onChange={(e) => setLocalMacAddress(e.target.value)}
            placeholder="AA:BB:CC:DD:EE:FF"
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              fontSize: '14px', 
              border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #cbd5e0', 
              borderRadius: '6px',
              backgroundColor: theme === 'dark' ? '#252d3a' : 'white',
              color: theme === 'dark' ? '#dae3ed' : '#2d3748',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* イベントタイプ: チェックボックスドロップダウンで複数選択 */}
        <div style={{ flex: 1, minWidth: '200px' }} ref={dropdownRef}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: theme === 'dark' ? '#dae3ed' : '#2d3748' }}>
            イベントタイプ
          </label>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setEventTypeDropdownOpen(prev => !prev)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #cbd5e0',
                borderRadius: '6px',
                backgroundColor: theme === 'dark' ? '#252d3a' : 'white',
                color: theme === 'dark' ? '#dae3ed' : '#2d3748',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>{displayLabel}</span>
              <span style={{ fontSize: '10px' }}>{eventTypeDropdownOpen ? '▲' : '▼'}</span>
            </button>

            {eventTypeDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 100,
                backgroundColor: theme === 'dark' ? '#252d3a' : 'white',
                border: theme === 'dark' ? '1px solid #3d4858' : '1px solid #cbd5e0',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                marginTop: '4px',
                padding: '8px 0'
              }}>
                {/* すべて選択 */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: theme === 'dark' ? '#dae3ed' : '#2d3748',
                  fontWeight: selectedTypes.length === 0 ? 'bold' : 'normal',
                  borderBottom: theme === 'dark' ? '1px solid #3d4858' : '1px solid #e2e8f0',
                  marginBottom: '4px'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedTypes.length === 0}
                    onChange={() => {
                      setEventLogFilters(prev => ({ ...prev, event_type: [] }));
                      setEventLogPage(1);
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  すべて
                </label>
                {/* 各イベントタイプ */}
                {EVENT_TYPES.map(type => (
                  <label key={type} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: theme === 'dark' ? '#dae3ed' : '#2d3748',
                    backgroundColor: selectedTypes.includes(type)
                      ? (theme === 'dark' ? 'rgba(66,153,225,0.15)' : 'rgba(66,153,225,0.08)')
                      : 'transparent'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleEventType(type)}
                      style={{ marginRight: '8px' }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* エラーのみ表示 */}
        <div>
          <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer', color: theme === 'dark' ? '#dae3ed' : '#2d3748' }}>
            <input
              type="checkbox"
              checked={eventLogFilters.error_only}
              onChange={(e) => {
                setEventLogFilters(prev => ({ ...prev, error_only: e.target.checked }));
                setEventLogPage(1);
              }}
              style={{ marginRight: '8px' }}
            />
            エラーのみ表示
          </label>
        </div>
      </div>

      {/* DataTable コンポーネント */}
      <DataTable
        columns={eventLogColumns}
        data={eventLogs}
        defaultColumns={defaultEventLogColumns}
        onColumnsChange={setEventLogColumns}
        localStorageKey="eventLogColumns"
        sortConfig={eventLogSort}
        onSortChange={setEventLogSort}
        selectedIds={selectedEventLogIds}
        onSelectionChange={setSelectedEventLogIds}
        idField="event_log_id"
        currentPage={eventLogPage}
        pageSize={eventLogPageSize}
        totalItems={eventLogTotal}
        onPageChange={setEventLogPage}
        onPageSizeChange={setEventLogPageSize}
        bulkActionButton={
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn btn-danger"
              onClick={handleDeleteSelectedEventLogs}
              disabled={selectedEventLogIds.length === 0}
              style={{
                backgroundColor: selectedEventLogIds.length === 0 ? '#cbd5e0' : '#f56565',
                cursor: selectedEventLogIds.length === 0 ? 'not-allowed' : 'pointer',
                opacity: selectedEventLogIds.length === 0 ? 0.6 : 1
              }}
            >
              選択した項目を削除 ({selectedEventLogIds.length})
            </button>
            {selectedEventLogIds.length > 0 && (
              <span style={{ color: '#718096', fontSize: '14px' }}>
                {selectedEventLogIds.length}件選択中
              </span>
            )}
          </div>
        }
        renderCell={renderEventLogCell}
        emptyMessage="イベントログがありません"
      />
    </div>
  );
}

export default EventLogs;
